import { useMemo } from 'react';
import { SxProps } from '@mui/material';
import { keyframes } from '@mui/system';

/**
 * Builds the glow keyframes for one pulse breath: invisible -> glow -> invisible.
 * Starting and ending on a zero-spread shadow means the resting state matches
 * `box-shadow: none`, so nothing snaps or pops when the animation starts/stops.
 *
 * @param borderColor - CSS color used for the glow.
 * @param borderWidth - Ring thickness, in pixels, at the peak of the glow.
 */
const buildPulseKeyframes = (
  borderColor: string,
  borderWidth: number,
) => keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 ${borderColor};
  }
  50% {
    box-shadow: 0 0 14px ${borderWidth + 3}px ${borderColor};
  }
`;

/**
 * Computes the wrapper `sx` for PulsingBorder, memoized on its visual inputs.
 *
 * @param borderColor - CSS color for the border and glow.
 * @param borderWidth - Border thickness in pixels.
 * @param borderRadius - Border radius passed through to the wrapper.
 * @param speed - Duration, in seconds, of a single glow breath while pulsing.
 * @param iterationCount - Number of whole breaths the animation runs before holding at rest.
 * @param isPulsing - Whether the glow animation should currently be running.
 * @param active - Whether pulsing is enabled at all; false hides the ring completely.
 * @param fadeDuration - Seconds any residual box-shadow change eases over as a fallback.
 * @returns `root`, the `sx` object for the wrapper element.
 */
export const useStyles = (
  borderColor: string,
  borderWidth: number,
  borderRadius: number | string,
  speed: number,
  iterationCount: number,
  isPulsing: boolean,
  active: boolean,
  fadeDuration: number,
): { root: SxProps } => {
  return useMemo(() => {
    const pulse = buildPulseKeyframes(borderColor, borderWidth);
    const isAnimating = active && isPulsing;

    return {
      // box-shadow instead of `border` so the ring is purely visual and never
      // resizes or reflows whatever it wraps.
      root: {
        display: 'inline-flex',
        boxSizing: 'border-box',
        borderRadius,
        // Only ever visible while the animation is actually running — the
        // ring vanishes both during the pause phase and when fully off, and
        // the zero-spread value here matches the keyframes' own 0%/100%
        // state, so there's nothing to snap when the animation toggles.
        boxShadow: isAnimating ? `0 0 0 0 ${borderColor}` : 'none',
        transition: `box-shadow ${fadeDuration}s ease`,
        // Finite iterationCount (never `infinite`) so the animation always
        // completes its last breath — easing back down to invisible —
        // instead of getting cut off mid-glow by the pulse-cycle timer.
        animation: isAnimating
          ? `${pulse} ${speed}s ease-in-out ${iterationCount}`
          : 'none',
      },
    };
  }, [
    borderColor,
    borderWidth,
    borderRadius,
    speed,
    iterationCount,
    isPulsing,
    active,
    fadeDuration,
  ]);
};
